import axios from "axios";
import { t } from "i18next";
import { useContext, useState } from "react";
import styled from "styled-components";
import {
  ButtonWithConfirmDialog,
  DeleteButtonWithText,
} from "../../../../../components/Buttons/Buttons";
import {
  DownloadIcon,
  LoadingIcon,
  ShareIcon,
} from "../../../../../components/Icons/Icons";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { WithPermission } from "../../../../../components/WithPermission/WithPermission";
import { endpoints } from "../../../../../endpoints";
import type { DataMutate } from "../../../../../types/types";
import type { Assets, PIMAssetsResponse } from "../../../../../types/types.PIM";

interface IStyledCalProps {
  disabled?: boolean;
}

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DownloadButtonWrapper = styled.div<IStyledCalProps>`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${({ theme }) => theme.fontSizes.regular};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
`;

const StyledDeleteButton = styled(DeleteButtonWithText)`
  .delete-button-text-container {
    gap: 4px;
  }
`;

interface BulkActionOnAssetsProps {
  rows: string[] | "all";
  resetSelectedRows: () => void;
  tenant_id: string;
  fetchAssetsForDownload: () => Promise<Assets[]>;
  fetchSelectedAssets: () => Assets[];
  mutateAssets: DataMutate<PIMAssetsResponse>;
  canDeleteAssets?: boolean;
  onOpenShareAssetModal: () => void;
}

export const BulkActionOnAssets: React.FC<BulkActionOnAssetsProps> = ({
  rows,
  resetSelectedRows,
  tenant_id,
  fetchSelectedAssets,
  fetchAssetsForDownload,
  mutateAssets,
  canDeleteAssets = true,
  onOpenShareAssetModal,
}) => {
  const { notifySuccess, notifyError } = useContext(Notifications);
  const [isLoading, setIsLoading] = useState(false);

  const resetDownloadState = () => {
    setIsLoading(false);
    resetSelectedRows();
  };

  const handleBulkAssetDownload = async (rows: string[] | "all") => {
    if (Array.isArray(rows) && rows.length > 0) {
      if (rows.length === 1) {
        if (fetchAssetsForDownload) {
          const assetsAllData = await fetchAssetsForDownload();
          const assetsId = assetsAllData.find((el) => el.id === rows[0]);
          if (assetsId) window.open(assetsId.signed_url, "_blank");
        }
      } else {
        setIsLoading(true);
        try {
          const response = await axios.post(
            endpoints.v2_storefronts_id_pim_assets_download(tenant_id),
            { data: rows }
          );
          const downloadId = response.data;
          if (downloadId.job_id) {
            await checkDownloadStatus(downloadId.job_id);
          }
        } catch (error) {
          resetDownloadState();
          notifyError(t("Error during the download process. Please try again"));
        }
      }
    }
  };

  const handleBulkAssetsDelete = async () => {
    setIsLoading(true);
    try {
      const {
        data: { job_id },
      } = await axios.post<{
        job_id: string;
        status: "processing" | "success" | "failed";
      }>(`/v2/tenants/${tenant_id}/pim/assets/bulk-delete`, {
        asset_ids: rows,
      });
      await checkDeleteStatus(job_id);
    } catch (error) {
      notifyError(t("Error during the delete process. Please try again."));
    }
  };

  const handleDownloadSuccess = async (downloadId: string) => {
    try {
      const response = await axios.get(
        endpoints.v2_storefronts_id_pim_assets_download_id(
          tenant_id,
          downloadId
        ),
        { responseType: "blob" }
      );

      const blob = response.data;
      const contentDisposition = response.headers["content-disposition"];
      const fileName = contentDisposition
        ? contentDisposition.split("filename=")[1].replace(/"/g, "")
        : "download.zip";

      const link = document.createElement("a");
      const url = window.URL.createObjectURL(blob);
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      resetDownloadState();
      notifySuccess(t("Download successful! Your file has been saved"));
    } catch (error) {
      resetDownloadState();
      notifyError(t("Failed to handle successful download. Please try again"));
    }
  };

  const checkDownloadStatus = async (downloadId: string) => {
    let timeoutId: NodeJS.Timeout | null = null;
    try {
      const response = await axios.get(
        endpoints.v2_storefronts_id_pim_assets_download_id_status(
          tenant_id,
          downloadId
        )
      );
      const data = response.data;

      if (data.status === "processing") {
        timeoutId = setTimeout(() => checkDownloadStatus(downloadId), 2000);
      } else if (data.status === "success") {
        await handleDownloadSuccess(downloadId);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      } else if (data.status === "failed") {
        resetDownloadState();
        notifyError(t("File compression failed. Please try again"));
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    } catch (error) {
      resetDownloadState();
      notifyError(t("Failed to check download status. Please try again"));
    }
  };

  const checkDeleteStatus = async (job_id: string) => {
    let timeoutId: NodeJS.Timeout | null = null;
    try {
      const {
        data: { status },
      } = await axios.get<{
        status: "processing" | "success" | "failed";
        deleted_count?: number;
        total?: number;
      }>(`/v2/tenants/${tenant_id}/pim/assets/bulk-delete/${job_id}/status`);

      if (status === "processing") {
        timeoutId = setTimeout(async () => {
          await checkDeleteStatus(job_id);
        }, 2000);
      } else if (status === "success") {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        notifySuccess(t("Assets deleted successfully"));
        resetSelectedRows();
        await mutateAssets();
        setIsLoading(false);
      } else {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        notifyError(t("Error during the delete process. Please try again."));
        setIsLoading(false);
      }
    } catch (error) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      notifyError(t("Error during the delete process. Please try again."));
      setIsLoading(false);
    }
  };

  return (
    <ButtonWrapper>
      {isLoading && <LoadingIcon width={22} height={22} />}
      <DownloadButtonWrapper onClick={() => onOpenShareAssetModal()}>
        <ShareIcon width={20} height={20} />
        {t("Share")}
      </DownloadButtonWrapper>
      {canDeleteAssets && (
        <WithPermission permission={"delete_assets"}>
          <ButtonWithConfirmDialog
            Button={(props) => (
              <StyledDeleteButton {...props} disabled={isLoading}>
                {t("Delete")}
              </StyledDeleteButton>
            )}
            testid={"delete-assets"}
            handleConfirm={handleBulkAssetsDelete}
            buttonText={t("Delete")}
            confirmMessage={t(
              `
          {{number_of_assets}} selected assets are associated with {{number_of_products}} products.
          Are you sure you want to delete them?
          `,
              {
                number_of_assets: Array.isArray(rows) ? rows.length : "All",
                number_of_products: (() => {
                  if (Array.isArray(rows)) {
                    const total_number_of_products = new Set(
                      fetchSelectedAssets().flatMap(
                        ({ product_ids }) => product_ids || []
                      )
                    );
                    return total_number_of_products.size > 0
                      ? total_number_of_products.size
                      : "no";
                  }
                  return "some";
                })(),
              }
            )}
          />
        </WithPermission>
      )}
      <DownloadButtonWrapper
        onClick={() => handleBulkAssetDownload(rows)}
        disabled={isLoading}
      >
        <DownloadIcon width={16} height={16} />
        {t("Download")}
      </DownloadButtonWrapper>
    </ButtonWrapper>
  );
};

export const fetchSelectedAssets = ({
  rows,
  selectedItems,
  tableData,
}: {
  rows: string[] | "all";
  selectedItems: Assets[];
  tableData: Assets[];
}) => {
  if (rows === "all") {
    return [];
  }
  const rows_obj = rows.reduce((acc, row_id) => {
    acc[row_id] = true;
    return acc;
  }, {} as Record<string, boolean>);
  const selected_rows = selectedItems.filter(({ id }) => rows_obj[id]);
  const missing_rows = rows.filter(
    (row_id) => !selected_rows.some(({ id }) => id === row_id)
  );
  if (!missing_rows.length) {
    return selected_rows;
  } else {
    const missing_items = missing_rows.map((row_id) => {
      const item = tableData.find(({ id }) => id === row_id);
      if (item) {
        return [item];
      }
      return [];
    });
    return [...selected_rows, ...missing_items.flat()];
  }
};
